import React, { useState, useEffect, useMemo } from 'react';
import {
  ChartColumn,
  Clock,
  ExternalLink,
  Loader2,
  Mail,
  MailOpen,
  MousePointerClick,
  TriangleAlert
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useEmailApi, EmailAnalyticsSummary, EmailEventDetails } from '../../../common/hooks/use-email-api.hook';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

interface EmailTrackingAnalyticsProps {
  email: string;
  dateRange?: {
    start: string;
    end: string;
  };
  onRefresh?: () => void;
}

/** The four stat tiles share one shell; only icon, label and value change. */
const MetricCard = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) => (
  <div className="flex h-full flex-col items-center rounded-xl border bg-card p-3 transition-colors hover:bg-muted/50">
    <div className="mb-1.5 flex items-center gap-1.5 text-muted-foreground [&_svg]:size-4">
      {icon}
      <span className="text-xs font-medium">{label}</span>
    </div>
    <span className="text-lg font-medium text-foreground">{value}</span>
  </div>
);

export const EmailTrackingAnalytics: React.FC<EmailTrackingAnalyticsProps> = ({
  email,
  dateRange,
  onRefresh
}) => {
  const { t } = useTranslation();
  const { getEmailAnalyticsSummary, getEmailEventsForContact } = useEmailApi();

  const [analytics, setAnalytics] = useState<EmailAnalyticsSummary | null>(null);
  const [events, setEvents] = useState<EmailEventDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const defaultDateRange = useMemo(() => ({
    start: dayjs().subtract(30, 'day').toISOString(),
    end: dayjs().toISOString()
  }), []);

  const currentDateRange = useMemo(() => dateRange || defaultDateRange, [dateRange, defaultDateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching email analytics for:', email, currentDateRange);

      const [analyticsData, eventsData] = await Promise.all([
        getEmailAnalyticsSummary(currentDateRange.start, currentDateRange.end, email),
        getEmailEventsForContact(email, currentDateRange.start, currentDateRange.end)
      ]);

      setAnalytics(analyticsData);
      setEvents(eventsData);
    } catch (err) {
      console.error('Error fetching email analytics:', err);
      setError('Failed to load email analytics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email) {
      fetchAnalytics();
    }
  }, [email, currentDateRange.start, currentDateRange.end]);

  const handleRefresh = () => {
    fetchAnalytics();
    onRefresh?.();
  };

  const getEventIcon = (eventType: string) => {
    const iconClass = "size-4 text-muted-foreground";

    switch (eventType) {
      case 'EmailSentEvent':
        return <Mail className={iconClass} />;
      case 'EmailOpenedEvent':
        return <MailOpen className={iconClass} />;
      case 'EmailLinkClickedEvent':
        return <MousePointerClick className={iconClass} />;
      default:
        return <Clock className={iconClass} />;
    }
  };

  const getEventTypeLabel = (eventType: string) => {
    switch (eventType) {
      case 'EmailSentEvent':
        return t('EMAIL_TRACKING.EVENT_TYPES.EMAIL_SENT');
      case 'EmailOpenedEvent':
        return t('EMAIL_TRACKING.EVENT_TYPES.EMAIL_OPENED');
      case 'EmailLinkClickedEvent':
        return t('EMAIL_TRACKING.EVENT_TYPES.EMAIL_LINK_CLICKED');
      default:
        return eventType.replace('Event', '');
    }
  };

  const getEventBadgeClass = (eventType: string) => {
    switch (eventType) {
      case 'EmailOpenedEvent':
        return 'border-success/40 text-success';
      case 'EmailLinkClickedEvent':
        return 'border-warning/40 text-warning';
      default:
        return '';
    }
  };

  const formatEventDescription = (event: EmailEventDetails) => {
    switch (event.eventType) {
      case 'EmailSentEvent':
        const subject = event.data.subject || t('MARKETING_EMAIL.UNTITLED_EMAIL');
        return `${t('EMAIL_TRACKING.EMAIL_SENT')}: "${subject}"`;
      case 'EmailOpenedEvent':
        return t('EMAIL_TRACKING.EMAIL_OPENED');
      case 'EmailLinkClickedEvent':
        const linkType = event.data.linkType || 'unknown';
        const productId = event.data.productId;
        if (linkType === 'product' && productId) {
          return `${t('EMAIL_TRACKING.PRODUCT_LINK_CLICKED')}: ${productId}`;
        }
        return `${t('EMAIL_TRACKING.LINK_CLICKED')}: ${linkType}`;
      default:
        return event.eventType;
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
          <p className="text-xs text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <TriangleAlert />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Alert>
        <AlertDescription>{t('EMAIL_TRACKING.NO_DATA_AVAILABLE')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="px-4 pt-2 pb-4">
      {/* Key Metrics Cards */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MetricCard
          icon={<Mail />}
          label={t('EMAIL_TRACKING.EMAILS_SENT')}
          value={analytics.totalEmailsSent}
        />
        <MetricCard
          icon={<MailOpen />}
          label={t('EMAIL_TRACKING.EMAILS_OPENED')}
          value={analytics.totalEmailsOpened}
        />
        <MetricCard
          icon={<MousePointerClick />}
          label={t('EMAIL_TRACKING.LINKS_CLICKED')}
          value={analytics.totalLinksClicked}
        />
        <MetricCard
          icon={<ChartColumn />}
          label={t('EMAIL_TRACKING.OPEN_RATE')}
          value={`${analytics.openRate}%`}
        />
      </div>

      {/* Recent Activity Section */}
      {events.length > 0 && (
        <div className="mt-4 mb-1 rounded-xl border bg-card">
          {/* Section Header */}
          <div className="border-b bg-muted/50 p-3">
            <p className="text-xs font-medium tracking-tight text-foreground">
              {t('EMAIL_TRACKING.RECENT_ACTIVITY')}
            </p>
          </div>

          {/* Activity List */}
          <div className="divide-y">
            {events.slice(0, 10).map((event) => (
              <div key={event._id} className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/50">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-muted">
                  {getEventIcon(event.eventType)}
                </span>
                <div className="min-w-0">
                  <p className="mb-0.5 text-xs font-medium text-foreground">
                    {formatEventDescription(event)}
                  </p>
                  <div className="mt-0.5 flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {dayjs(event.dateTime).format('MMM D, YYYY h:mm A')}
                    </span>
                    <Badge
                      variant="outline"
                      className={cn('h-auto px-1.5 py-0 text-[10px]', getEventBadgeClass(event.eventType))}
                    >
                      {getEventTypeLabel(event.eventType)}
                    </Badge>
                    {event.data.originalUrl && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon-xs" aria-label={event.data.originalUrl}>
                            <ExternalLink />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm break-all">{event.data.originalUrl}</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Activity Message */}
      {events.length === 0 && (
        <div className="mb-1 flex flex-col items-center rounded-xl border bg-card p-6 text-center">
          <Clock className="mb-2 size-10 text-muted-foreground opacity-35" />
          <p className="text-xs text-muted-foreground">
            {t('EMAIL_TRACKING.NO_ACTIVITY_FOUND')}
          </p>
        </div>
      )}
    </div>
  );
};

export default EmailTrackingAnalytics;
